/**
 * The splash messages a brand and a location show before ordering (ORDR-164,
 * over paio-ordering ORDR-163).
 *
 *   GET /ordering/splash/?brand=<id>      [brand]
 *   GET /ordering/splash/?location=<id>   [brand, location]
 *
 * Exactly one parameter, once (the server's 400 otherwise). Each item is
 * `{level, title, message, image_url}`, and an empty list means no splash.
 *
 * A SPLASH MUST NEVER BLOCK ORDERING. Every failure, a 4xx, a 5xx, a network
 * failure, a throw or a body this parser refuses, is the empty list, logged
 * with the correlation id. The message is plain text, and it stays a string
 * here: nothing in this path, or in `Splash.tsx`, ever parses it as HTML.
 * An image that is not `https://` is dropped, a second guard after the
 * server's own host rule.
 */

import type { ApiClient } from "../api/client.js";

export type SplashLevel = "brand" | "location";

export interface SplashTarget {
  readonly level: SplashLevel;
  readonly nodeId: string;
}

export interface SplashMessage {
  /** `BRAND` or `LOCATION`, as the server sent it. */
  readonly level: string;
  readonly title: string;
  readonly message: string;
  readonly imageUrl: string | null;
}

function nonBlank(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function parseMessage(raw: unknown): SplashMessage | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const { level, title, message, image_url: image } = raw as Record<string, unknown>;
  if (!nonBlank(level) || !nonBlank(title) || !nonBlank(message)) {
    return null;
  }
  return {
    level,
    title,
    message,
    imageUrl: typeof image === "string" && image.toLowerCase().startsWith("https://") ? image : null,
  };
}

export async function readSplash(client: ApiClient, target: SplashTarget): Promise<readonly SplashMessage[]> {
  const query = new URLSearchParams({ [target.level]: target.nodeId });
  try {
    const response = await client.request(`/ordering/splash/?${query.toString()}`);
    if (response.status !== 200) {
      if (response.status !== null) {
        console.warn(`[splash] read answered ${response.status} (${response.correlationId}); no splash`);
      }
      return [];
    }
    const data =
      typeof response.body === "object" && response.body !== null && "data" in response.body
        ? response.body.data
        : undefined;
    const messages = Array.isArray(data) ? data.map(parseMessage) : [null];
    if (messages.some((message) => message === null)) {
      console.warn(`[splash] read answer refused (${response.correlationId}); no splash`);
      return [];
    }
    return messages as SplashMessage[];
  } catch (caught) {
    console.warn("[splash] read failed; no splash", caught);
    return [];
  }
}
